/**
 * answerStatusView.js
 *
 * シーズン管理者（role 7）の「回答状況指定」（2026-10 追加パッチ・feature/answerStatusView）。
 * サーバー rcdr_server/utils/answerStatusView.js と同じ決まり。
 *
 * - 対象は role 7 だけ。診断の種類ごとに userData.answerStatusView（/api/users/auth）の値が false のときだけ見られない
 *   （値が無い既存ユーザーはすべて見られる）。
 *     engagement … エンゲージメント（30010・90301）: 「メンバー回答状況」でシーズンを選べるか
 *     team       … 360度系（40010 360度診断・60020 フォロワーシップ）: 「チーム回答状況」のメニュー
 *                   （「メンバー回答状況」の 360度系シーズンも同じ）
 *     other      … その他の診断: 「メンバー回答状況」でシーズンを選べるか
 * - エンゲージメント・その他の両方が見られないと「メンバー回答状況」、360度系が見られないと「チーム回答状況」の
 *   メニューを出さない（LeftMenu.js の answerStatus）。URL で開いてもトップへ戻す。
 * - シーズン指定（accessibleSurveySeasons）は、エンゲージメントでは「結果（組織結果）を見るシーズン」なので
 *   回答状況の画面では使わない。360度系・その他は今までどおりシーズン指定で絞る（utils/seasonFilter.js と同じ。
 *   0 件 = 制限なし）。
 * - サーバーも見られない回答状況を返さない。画面側の絞り込みは二重の備え。
 */

const ENGAGEMENT_CODES = ['30010', '90301'];
const TEAM_CODES = ['40010', '60020'];

/**
 * 診断コードから回答状況指定の種類を返す。
 * @param {string} surveyCode
 * @returns {'engagement' | 'team' | 'other'}
 */
export const answerStatusKindOf = (surveyCode) => {
  const code = String(surveyCode == null ? '' : surveyCode);
  if (ENGAGEMENT_CODES.includes(code)) return 'engagement';
  if (TEAM_CODES.includes(code)) return 'team';
  return 'other';
};

/**
 * 種類ごとに回答状況を見られるか（role 7 以外はすべて見られる）。
 * @param {Object} userData - usr.userData（role と answerStatusView を参照）
 * @returns {{ engagement: boolean, team: boolean, other: boolean }}
 */
export const answerStatusViewOf = (userData) => {
  const view = (userData && userData.role === 7 && userData.answerStatusView) || {};
  return {
    engagement: view.engagement !== false,
    team: view.team !== false,
    other: view.other !== false,
  };
};

/**
 * 「メンバー回答状況」を出さないか（エンゲージメント・その他の両方が見られない）。
 * @param {Object} userData
 * @returns {boolean}
 */
export const hidesMemberStatus = (userData) => {
  const view = answerStatusViewOf(userData);
  return !view.engagement && !view.other;
};

/**
 * 「チーム回答状況」を出さないか（360度系が見られない）。
 * @param {Object} userData
 * @returns {boolean}
 */
export const hidesTeamStatus = (userData) => !answerStatusViewOf(userData).team;

// シーズン ID（文字列）の集合。role 7 以外・シーズン指定 0 件は null（制限なし）
const accessibleSeasonSet = (userData) => {
  if (!userData || userData.role !== 7) return null;
  const allowed = userData.accessibleSurveySeasons || [];
  return allowed.length > 0 ? new Set(allowed.map((id) => String(id))) : null;
};

// 診断コードとシーズン ID の組を、回答状況の画面に出してよいか
const statusVisible = (surveyCode, seasonId, view, seasons) => {
  const kind = answerStatusKindOf(surveyCode);
  if (!view[kind]) return false;
  if (kind === 'engagement' || !seasons) return true;
  return seasons.has(String(seasonId));
};

/**
 * 回答状況の画面（メンバー回答状況）に出すシーズンだけを残す（順序保持）。
 * @param {Array<{ _id: string, survey_code: string }>} seasons
 * @param {Object} userData
 * @returns {Array}
 */
export const filterStatusSeasons = (seasons, userData) => {
  if (!Array.isArray(seasons)) return [];
  if (!userData || userData.role !== 7) return seasons;
  const view = answerStatusViewOf(userData);
  const allowed = accessibleSeasonSet(userData);
  return seasons.filter((s) => s && statusVisible(s.survey_code, s._id, view, allowed));
};

/**
 * 回答状況の画面に出すチケットだけを残す（順序保持）。
 * @param {Array<{ survey_code: string, survey_season_id: string }>} tokens
 * @param {Object} userData
 * @returns {Array}
 */
export const filterStatusTokens = (tokens, userData) => {
  if (!Array.isArray(tokens)) return [];
  if (!userData || userData.role !== 7) return tokens;
  const view = answerStatusViewOf(userData);
  const allowed = accessibleSeasonSet(userData);
  return tokens.filter((t) => t && statusVisible(t.survey_code, t.survey_season_id, view, allowed));
};

/**
 * 回答状況の画面（メンバー回答状況）の一覧にこの人を出すか。
 * role 7 で絞り込みがある（見られない種類がある・シーズン指定がある）ときは、出せるチケットを 1 枚以上持つ人だけ。
 * 絞り込みが無いときは全員（従来どおり）。
 * @param {Object} user - survey_token を持つユーザー
 * @param {Object} userData
 * @returns {boolean}
 */
export const userHasStatusToken = (user, userData) => {
  if (!userData || userData.role !== 7) return true;
  const view = answerStatusViewOf(userData);
  const restricted = !view.engagement || !view.team || !view.other || !!accessibleSeasonSet(userData);
  if (!restricted) return true;
  return filterStatusTokens(user && user.survey_token, userData).length > 0;
};
